import React, { useState, useEffect, useRef } from "react";
import { db } from "../../firebase";
import { collection, getDocs, doc, deleteDoc, setDoc } from "firebase/firestore";
import { parseAndUploadHSCodes } from "../../utils/excelParser";
import { UploadCloud, Search, Trash2, Edit2, Loader2, Save, X, Plus } from "lucide-react";

export default function SuperAdminHSCodes() {
    const [hsCodes, setHsCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const fileInputRef = useRef(null);

    const [editingCode, setEditingCode] = useState(null);
    const [editForm, setEditForm] = useState({ description: "", dutyRate: "", gstRate: "" });

    const [isAddingNew, setIsAddingNew] = useState(false);
    const [newForm, setNewForm] = useState({ code: "", description: "", dutyRate: "", gstRate: "" });

    const fetchHSCodes = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, "hsCodes"));
            const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setHsCodes(data);
        } catch (err) {
            console.error("Error fetching HS Codes:", err);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchHSCodes();
    }, []);

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        try {
            const result = await parseAndUploadHSCodes(file);
            alert(`Successfully uploaded ${result.count} HS codes.`);
            if (result.errors.length > 0) {
                console.warn("Upload Warnings:", result.errors);
                alert(`Finished with ${result.errors.length} warnings. Check console.`);
            }
            fetchHSCodes(); // Refresh table
        } catch (err) {
            alert("Error parsing file. Ensure it is a valid .csv or .xlsx file.");
        }
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleDelete = async (id) => {
        if (!window.confirm(`Are you sure you want to delete HS Code ${id}?`)) return;
        try {
            await deleteDoc(doc(db, "hsCodes", id));
            setHsCodes(hsCodes.filter(h => h.id !== id));
        } catch (err) {
            console.error("Error deleting:", err);
            alert("Failed to delete HS Code.");
        }
    };

    const handleEditClick = (item) => {
        setEditingCode(item.id);
        setEditForm({
            description: item.description,
            dutyRate: item.dutyRate,
            gstRate: item.gstRate
        });
    };

    const saveEdit = async (id) => {
        try {
            const payload = {
                description: editForm.description,
                dutyRate: parseFloat(editForm.dutyRate) || 0,
                gstRate: parseFloat(editForm.gstRate) || 10,
                updatedAt: new Date().toISOString()
            };
            await setDoc(doc(db, "hsCodes", id), payload, { merge: true });

            setHsCodes(hsCodes.map(h => h.id === id ? { ...h, ...payload } : h));
            setEditingCode(null);
        } catch (err) {
            console.error("Error saving edit:", err);
            alert("Failed to save changes.");
        }
    };

    const saveNewCode = async () => {
        if (!newForm.code || !newForm.description) return alert("HS Code and Description are required.");

        try {
            const cleanCode = newForm.code.replace(/[^0-9]/g, "");
            if (!cleanCode) return alert("Invalid HS Code format.");

            const payload = {
                description: newForm.description,
                dutyRate: parseFloat(newForm.dutyRate) || 0,
                gstRate: parseFloat(newForm.gstRate) || 10,
                updatedAt: new Date().toISOString()
            };
            await setDoc(doc(db, "hsCodes", cleanCode), payload);

            setHsCodes([{ id: cleanCode, ...payload }, ...hsCodes]);
            setIsAddingNew(false);
            setNewForm({ code: "", description: "", dutyRate: "", gstRate: "" });
        } catch (err) {
            console.error("Error adding new code:", err);
            alert("Failed to add new HS Code.");
        }
    };

    const filtered = hsCodes.filter(h =>
        h.id.includes(searchTerm) ||
        (h.description && h.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            <div className="flex items-center justify-between bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <div>
                    <h1 className="text-2xl font-bold text-navy-900 font-serif">HS Code Database</h1>
                    <p className="text-slate-500 mt-1">Manage global tariff and duty parameters.</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => setIsAddingNew(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-white text-navy-900 border border-slate-200 font-medium rounded hover:bg-slate-50 transition-colors"
                    >
                        <Plus size={16} /> Add Code
                    </button>
                    <input
                        type="file"
                        accept=".csv, .xlsx, .xls"
                        className="hidden"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                    />
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex items-center gap-2 px-6 py-2 bg-navy-900 text-white font-bold rounded hover:bg-navy-800 transition-colors disabled:opacity-50"
                    >
                        {uploading ? <Loader2 size={18} className="animate-spin" /> : <UploadCloud size={18} />}
                        Upload Excel Dictionary
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                    <h2 className="font-bold text-navy-900">Database Records ({hsCodes.length})</h2>
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search code or description..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 pr-3 py-1.5 text-sm bg-white border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-gold-500 w-64"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <Loader2 className="animate-spin text-slate-400" size={32} />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-medium">
                                <tr>
                                    <th className="px-6 py-3 w-40">HS Code</th>
                                    <th className="px-6 py-3">Description</th>
                                    <th className="px-6 py-3 w-32">Duty Rate (%)</th>
                                    <th className="px-6 py-3 w-32">GST (%)</th>
                                    <th className="px-6 py-3 w-32 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {isAddingNew && (
                                    <tr className="bg-emerald-50">
                                        <td className="px-6 py-3">
                                            <input
                                                type="text"
                                                value={newForm.code}
                                                onChange={e => setNewForm({ ...newForm, code: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-gold-500 font-mono"
                                                placeholder="e.g. 950300"
                                                autoFocus
                                            />
                                        </td>
                                        <td className="px-6 py-3">
                                            <input
                                                type="text"
                                                value={newForm.description}
                                                onChange={e => setNewForm({ ...newForm, description: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-gold-500"
                                                placeholder="Description"
                                            />
                                        </td>
                                        <td className="px-6 py-3">
                                            <input
                                                type="number"
                                                value={newForm.dutyRate}
                                                onChange={e => setNewForm({ ...newForm, dutyRate: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-gold-500"
                                                placeholder="0"
                                            />
                                        </td>
                                        <td className="px-6 py-3">
                                            <input
                                                type="number"
                                                value={newForm.gstRate}
                                                onChange={e => setNewForm({ ...newForm, gstRate: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-gold-500"
                                                placeholder="10"
                                            />
                                        </td>
                                        <td className="px-6 py-3 text-right space-x-2">
                                            <button onClick={saveNewCode} className="p-1.5 text-emerald-600 hover:bg-emerald-100 rounded">
                                                <Save size={16} />
                                            </button>
                                            <button onClick={() => setIsAddingNew(false)} className="p-1.5 text-slate-400 hover:bg-slate-200 rounded">
                                                <X size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                )}

                                {filtered.map((item) => (
                                    <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                                        {editingCode === item.id ? (
                                            <>
                                                <td className="px-6 py-3 font-mono font-bold text-navy-900">{item.id}</td>
                                                <td className="px-6 py-3">
                                                    <input
                                                        type="text"
                                                        value={editForm.description}
                                                        onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                                                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-gold-500"
                                                    />
                                                </td>
                                                <td className="px-6 py-3">
                                                    <input
                                                        type="number"
                                                        value={editForm.dutyRate}
                                                        onChange={e => setEditForm({ ...editForm, dutyRate: e.target.value })}
                                                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-gold-500"
                                                    />
                                                </td>
                                                <td className="px-6 py-3">
                                                    <input
                                                        type="number"
                                                        value={editForm.gstRate}
                                                        onChange={e => setEditForm({ ...editForm, gstRate: e.target.value })}
                                                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-gold-500"
                                                    />
                                                </td>
                                                <td className="px-6 py-3 text-right space-x-2">
                                                    <button onClick={() => saveEdit(item.id)} className="p-1.5 text-emerald-600 hover:bg-emerald-100 rounded">
                                                        <Save size={16} />
                                                    </button>
                                                    <button onClick={() => setEditingCode(null)} className="p-1.5 text-slate-400 hover:bg-slate-200 rounded">
                                                        <X size={16} />
                                                    </button>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="px-6 py-4 font-mono font-bold text-navy-900">{item.id}</td>
                                                <td className="px-6 py-4 text-slate-700">{item.description}</td>
                                                <td className="px-6 py-4">{item.dutyRate}%</td>
                                                <td className="px-6 py-4">{item.gstRate}%</td>
                                                <td className="px-6 py-4 text-right space-x-2">
                                                    <button
                                                        onClick={() => handleEditClick(item)}
                                                        className="p-1.5 text-slate-400 hover:text-navy-900 hover:bg-slate-100 rounded transition-colors inline-block"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(item.id)}
                                                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors inline-block"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
